import { createFileRoute } from "@tanstack/react-router";

import { useConsultationSettings } from "@/hooks/useAdminAppointments";
import { Loader2, Plus, Settings2, Clock, Ban, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";


export const Route = createFileRoute("/_admin/admin/consultation-settings")({
  component: ConsultationSettingsPage,
});

function ConsultationSettingsPage() {
  const { 
    data: settings, 
    isLoading,
    refetch,
    createType,
    updateType,
    deleteType,
    createAvailability,
    updateAvailability,
    deleteAvailability
  } = useConsultationSettings();


  const [editingType, setEditingType] = useState<any>(null);
  const [deletingType, setDeletingType] = useState<any>(null);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);

  const [editingAvail, setEditingAvail] = useState<any>(null);
  const [deletingAvail, setDeletingAvail] = useState<any>(null);
  const [isAvailDialogOpen, setIsAvailDialogOpen] = useState(false);

  const handleSaveType = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get("name") as string,
      slug: (formData.get("name") as string).toLowerCase().replace(/\s+/g, '-'),
      description: formData.get("description") as string,
      durationMinutes: parseInt(formData.get("duration") as string),
      pricePaise: parseFloat(formData.get("price") as string) * 100,
      isActive: formData.get("isActive") === "on",
      isFree: formData.get("isFree") === "on",
    };

    try {
      if (editingType?.id) {
        await updateType.mutateAsync({ id: editingType.id, ...data });
        toast.success("Consultation type updated");
      } else {
        await createType.mutateAsync(data);
        toast.success("Consultation type created");
      }
      setIsTypeDialogOpen(false);
      setEditingType(null);
    } catch (err) {
      toast.error("Failed to save consultation type");
    }
  };

  const handleDeleteType = async () => {
    try {
      await deleteType.mutateAsync({ id: deletingType.id });
      toast.success("Consultation type deleted");
      setDeletingType(null);
    } catch (err) {
      toast.error("Failed to delete consultation type");
    }
  };

  const handleSaveAvail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const normalizeTime = (t: string) => {
      if (!t) return t;
      // If it's HH:mm, add :00
      if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
      return t;
    };

    // IMPORTANT: Get ID directly from editingAvail state as it's the source of truth for the active edit
    const recordId = editingAvail?.id;
    
    // Extract values from form, fallback to state if field is disabled/not present
    const consultantId = (formData.get("consultantId") || editingAvail?.consultant_id) as string;
    const dayOfWeekStr = (formData.get("dayOfWeek") || (editingAvail?.day_of_week !== undefined ? String(editingAvail.day_of_week) : "1")) as string;
    const dayOfWeek = parseInt(dayOfWeekStr);
    const startTimeInput = formData.get("startTime") as string;
    const endTimeInput = formData.get("endTime") as string;
    
    const startTime = normalizeTime(startTimeInput);
    const endTime = normalizeTime(endTimeInput);

    const payload = {
      id: recordId,
      consultantId,
      dayOfWeek,
      startTime,
      endTime
    };

    console.log("Saving availability with exact payload:", payload);

    try {
      if (recordId) {
        console.log("[handleSaveAvail] Triggering UPDATE for ID:", recordId);
        const result = await updateAvailability.mutateAsync(payload);
        console.log("[handleSaveAvail] Server Response Data:", result);
        
        if (result?.success && result?.data) {
          const verified = result.data;
          console.log("[handleSaveAvail] Verification confirmed by server:", verified);
          toast.success(`Availability updated: ${verified.start_time} - ${verified.end_time}`);
        } else {
          toast.success("Availability updated");
        }
      } else {
        console.log("[handleSaveAvail] Triggering CREATE");
        const result = await createAvailability.mutateAsync(payload);
        console.log("[handleSaveAvail] Create mutation result:", result);
        toast.success("Availability created");
      }
      setIsAvailDialogOpen(false);
      setEditingAvail(null);
      // Force a manual refetch just in case React Query is being stubborn
      refetch();


    } catch (err: any) {
      console.error("[handleSaveAvail] Caught error during save:", err);
      // Check if it was actually a success hidden as an error (e.g., stale select result)
      // If we got here, mutation.mutateAsync threw.
      toast.error(err.message || "Failed to save availability");
    }
  };

  const handleDeleteAvail = async () => {
    try {
      await deleteAvailability.mutateAsync({ id: deletingAvail.id });
      toast.success("Availability deleted");
      setDeletingAvail(null);
    } catch (err) {
      toast.error("Failed to delete availability");
    }
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Consultation Settings</h1>
            <p className="text-sm text-muted-foreground">Configure types, consultants and availability.</p>
          </div>
        </header>

        {isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-medium">
                  <Settings2 className="h-5 w-5 text-primary" />
                  Consultation Types
                </h2>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 gap-1"
                  onClick={() => {
                    setEditingType({});
                    setIsTypeDialogOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add
                </Button>
              </div>
              <div className="grid gap-3">
                {settings?.types.map((type) => (
                  <Card key={type.id}>
                    <CardHeader className="p-4 pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{type.name}</CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge variant={type.is_active ? "outline" : "secondary"}>
                            {type.is_active ? "Active" : "Inactive"}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => {
                              setEditingType(type);
                              setIsTypeDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive"
                            onClick={() => setDeletingType(type)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 pt-0 text-sm">
                      <p className="text-muted-foreground">{type.description}</p>
                      <div className="mt-2 flex items-center gap-4 text-xs font-medium">
                        <span>{type.duration_minutes} mins</span>
                        <span className="text-primary">₹{(type.price_paise / 100).toFixed(0)}</span>
                        {type.is_free && <Badge variant="secondary" className="text-[10px]">Free</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-medium">
                  <Clock className="h-5 w-5 text-primary" />
                  Availability Rules
                </h2>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 gap-1"
                  onClick={() => {
                    setEditingAvail({});
                    setIsAvailDialogOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Configure
                </Button>
              </div>
              <div className="grid gap-4">
                {(() => {
                  const grouped = (settings?.availability || []).reduce((acc: any, rule: any) => {
                    const key = `${rule.consultant_id}-${rule.day_of_week}`;
                    if (!acc[key]) acc[key] = { 
                      consultantId: rule.consultant_id,
                      consultantName: rule.consultants?.name, 
                      dayOfWeek: rule.day_of_week,
                      slots: [] 
                    };
                    acc[key].slots.push(rule);
                    return acc;
                  }, {});

                  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

                  return Object.values(grouped).map((group: any) => (
                    <Card key={`${group.consultantId}-${group.dayOfWeek}`} className="overflow-hidden">
                      <CardHeader className="bg-muted/30 p-4 pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <CardTitle className="text-base">{group.consultantName}</CardTitle>
                            <span className="text-xs font-medium text-muted-foreground">{dayNames[group.dayOfWeek]}</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-8 gap-1 text-xs"
                            onClick={() => {
                              setEditingAvail({ consultant_id: group.consultantId, day_of_week: group.dayOfWeek });
                              setIsAvailDialogOpen(true);
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add Time Slot
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 pt-2">
                        <div className="divide-y divide-border">
                          {group.slots.sort((a: any, b: any) => a.start_time.localeCompare(b.start_time)).map((slot: any) => (
                            <div key={slot.id} className="flex items-center justify-between py-2 first:pt-0 last:pb-0">
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-medium">
                                  {new Date(`2000-01-01T${slot.start_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                  {" — "}
                                  {new Date(`2000-01-01T${slot.end_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7"
                                  onClick={() => {
                                    setEditingAvail(slot);
                                    setIsAvailDialogOpen(true);
                                  }}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => setDeletingAvail(slot)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ));
                })()}
              </div>

              <div className="flex items-center justify-between pt-4">
                <h2 className="flex items-center gap-2 text-lg font-medium">
                  <Ban className="h-5 w-5 text-destructive" />
                  Blocked Dates
                </h2>
              </div>
              <div className="grid gap-3">
                {settings?.blocked.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">No dates blocked.</p>
                ) : (
                  settings?.blocked.map((b) => (
                    <Card key={b.id}>
                      <CardContent className="p-4 text-sm">
                        <span className="font-medium">{(b as any).consultants?.name}</span>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {new Date(b.start_time).toLocaleDateString()} - {b.reason || "No reason"}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Consultation Type Dialog */}
      <Dialog open={isTypeDialogOpen} onOpenChange={setIsTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType?.id ? "Edit Consultation Type" : "Add Consultation Type"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveType} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" name="name" defaultValue={editingType?.name} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" name="description" defaultValue={editingType?.description} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input id="duration" name="duration" type="number" defaultValue={editingType?.duration_minutes || 60} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price (₹)</Label>
                <Input id="price" name="price" type="number" defaultValue={editingType ? (editingType.price_paise / 100) : 0} required />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox id="isActive" name="isActive" defaultChecked={editingType?.is_active ?? true} />
                <Label htmlFor="isActive">Active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="isFree" name="isFree" defaultChecked={editingType?.is_free ?? false} />
                <Label htmlFor="isFree">Free</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTypeDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={updateType.isPending || createType.isPending}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={isAvailDialogOpen} onOpenChange={(o) => {
        setIsAvailDialogOpen(o);
        if (!o) setEditingAvail(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAvail?.id ? "Edit Time Slot" : "Add Time Slot"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveAvail} className="space-y-4">
            {editingAvail?.id && (
              <input type="hidden" name="availabilityId" value={editingAvail.id} />
            )}
            <div className="space-y-2">
              <Label htmlFor="consultantId">Consultant</Label>
              <Select 
                name="consultantId" 
                defaultValue={editingAvail?.consultant_id || settings?.consultants[0]?.id}
                disabled={!!editingAvail?.consultant_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select consultant" />
                </SelectTrigger>
                <SelectContent>
                  {settings?.consultants.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dayOfWeek">Day of Week</Label>
              <Select 
                name="dayOfWeek" 
                defaultValue={(editingAvail?.day_of_week ?? 1).toString()}
                disabled={editingAvail?.day_of_week !== undefined}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sunday</SelectItem>
                  <SelectItem value="1">Monday</SelectItem>
                  <SelectItem value="2">Tuesday</SelectItem>
                  <SelectItem value="3">Wednesday</SelectItem>
                  <SelectItem value="4">Thursday</SelectItem>
                  <SelectItem value="5">Friday</SelectItem>
                  <SelectItem value="6">Saturday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time</Label>
                <Input id="startTime" name="startTime" type="time" defaultValue={editingAvail?.start_time?.slice(0, 5) || "09:00"} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">End Time</Label>
                <Input id="endTime" name="endTime" type="time" defaultValue={editingAvail?.end_time?.slice(0, 5) || "18:00"} required />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAvailDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={updateAvailability.isPending || createAvailability.isPending}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Type Confirmation */}
      <AlertDialog open={!!deletingType} onOpenChange={(open) => !open && setDeletingType(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Consultation Type?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingType?.name}". Existing appointments will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteType} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Avail Confirmation */}
      <AlertDialog open={!!deletingAvail} onOpenChange={(open) => !open && setDeletingAvail(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Availability Rule?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this availability rule?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAvail} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
